import type { EventReadinessCheck, JobEventLite } from '@/gen/cortado/v1/jobs_pb'
import { EventReadinessCheckEnum, EventReadinessStateEnum } from '@/gen/cortado/v1/jobs_pb'

/**
 * How a Logistics row reads:
 * - `done`: the step is finished (filled success mark).
 * - `todo`: the step still needs doing (dashed outline mark).
 * - `warning`: the step needs a look — details changed since a saved
 *   timeline or packing list was made, or a calendar sync failed.
 * - `info`: nothing to do (no questionnaire on the booking, a stage the
 *   calendar settings don't sync) or the server couldn't say; not counted.
 */
export type LogisticsMark = 'done' | 'todo' | 'warning' | 'info'

export type LogisticsCheckKey = 'staff' | 'questionnaire' | 'timeline' | 'inventory' | 'directions' | 'calendar'

export interface LogisticsCheck {
  key: LogisticsCheckKey
  label: string
  mark: LogisticsMark
  /** The sub-line under the label, written by the server. */
  detail: string
  /** The row's action button label. */
  action: string
  /** Wording for a multi-event row's "Next: …" when this step is the first not done. */
  nextStep: string
}

/** Display order of the checklist, matching the server's. */
export const LOGISTICS_CHECK_ORDER: LogisticsCheckKey[] = [
  'staff',
  'questionnaire',
  'timeline',
  'inventory',
  'directions',
  'calendar',
]

export const LOGISTICS_CHECK_LABELS: Record<LogisticsCheckKey, string> = {
  staff: 'Staff',
  questionnaire: 'Questionnaire',
  timeline: 'Timeline',
  inventory: 'Inventory',
  directions: 'Directions',
  calendar: 'Team calendar',
}

const KEY_BY_CHECK: Partial<Record<EventReadinessCheckEnum, LogisticsCheckKey>> = {
  [EventReadinessCheckEnum.EVENT_READINESS_CHECK_STAFF]: 'staff',
  [EventReadinessCheckEnum.EVENT_READINESS_CHECK_QUESTIONNAIRE]: 'questionnaire',
  [EventReadinessCheckEnum.EVENT_READINESS_CHECK_TIMELINE]: 'timeline',
  [EventReadinessCheckEnum.EVENT_READINESS_CHECK_INVENTORY]: 'inventory',
  [EventReadinessCheckEnum.EVENT_READINESS_CHECK_DIRECTIONS]: 'directions',
  [EventReadinessCheckEnum.EVENT_READINESS_CHECK_CALENDAR]: 'calendar',
}

const MARK_BY_STATE: Partial<Record<EventReadinessStateEnum, LogisticsMark>> = {
  [EventReadinessStateEnum.EVENT_READINESS_STATE_DONE]: 'done',
  [EventReadinessStateEnum.EVENT_READINESS_STATE_TODO]: 'todo',
  [EventReadinessStateEnum.EVENT_READINESS_STATE_ATTENTION]: 'warning',
  [EventReadinessStateEnum.EVENT_READINESS_STATE_NOT_NEEDED]: 'info',
}

/** The button label and "Next: …" wording for a check in a given state. */
function logisticsActions(key: LogisticsCheckKey, mark: LogisticsMark): { action: string; nextStep: string } {
  switch (key) {
    case 'staff':
      return { action: mark === 'done' ? 'Change' : 'Assign staff', nextStep: 'Assign staff' }
    case 'questionnaire':
      return mark === 'info'
        ? { action: 'Add questions', nextStep: 'Add questions' }
        : { action: 'Edit', nextStep: 'Get questionnaire answers' }
    case 'timeline':
      if (mark === 'warning') return { action: 'Review', nextStep: 'Review timeline' }
      return { action: mark === 'done' ? 'View' : 'Build timeline', nextStep: 'Build timeline' }
    case 'inventory':
      if (mark === 'warning') return { action: 'Review', nextStep: 'Review packing list' }
      return { action: mark === 'done' ? 'View' : 'Pack', nextStep: 'Finish packing' }
    case 'directions':
      return { action: mark === 'done' ? 'View' : 'Set up', nextStep: 'Set directions' }
    case 'calendar':
      if (mark === 'warning') return { action: 'Fix', nextStep: 'Fix calendar sync' }
      return { action: mark === 'todo' ? 'Add' : 'View', nextStep: 'Add to calendars' }
  }
}

function toCheck(key: LogisticsCheckKey, mark: LogisticsMark, detail: string): LogisticsCheck {
  return { key, label: LOGISTICS_CHECK_LABELS[key], mark, detail, ...logisticsActions(key, mark) }
}

/** One server readiness check as a Logistics row; undefined for an unknown check. */
export function logisticsCheckFromReadiness(check: EventReadinessCheck): LogisticsCheck | undefined {
  const key = KEY_BY_CHECK[check.check]
  if (!key) return undefined
  return toCheck(key, MARK_BY_STATE[check.state] ?? 'info', check.detail)
}

/** Whether a check has a done state at all (so it counts toward "n of m ready"). */
export function isStatefulCheck(check: LogisticsCheck): boolean {
  return check.mark !== 'info'
}

/**
 * The event's six checks in display order (Staff, Questionnaire, Timeline,
 * Inventory, Directions, Team calendar), from the server's readiness. When
 * the server sent none (it could not compute it), every row reads
 * "Status unavailable" and nothing is counted.
 */
export function logisticsChecklist(event: JobEventLite): LogisticsCheck[] {
  const byKey = new Map<LogisticsCheckKey, LogisticsCheck>()
  for (const check of event.readiness?.checks ?? []) {
    const row = logisticsCheckFromReadiness(check)
    if (row) byKey.set(row.key, row)
  }
  return LOGISTICS_CHECK_ORDER.map((key) => byKey.get(key) ?? toCheck(key, 'info', 'Status unavailable'))
}

/** "n of m ready" over the checks that have a done state. */
export function logisticsReadyCount(checks: LogisticsCheck[]): { ready: number; total: number } {
  const stateful = checks.filter(isStatefulCheck)
  return { ready: stateful.filter((check) => check.mark === 'done').length, total: stateful.length }
}

/** The booking-wide questionnaire row (every event of a booking reports the same one). */
export function logisticsQuestionnaireCheck(events: JobEventLite[]): LogisticsCheck {
  for (const event of events) {
    const check = logisticsChecklist(event).find((row) => row.key === 'questionnaire')
    if (check && check.detail !== 'Status unavailable') return check
  }
  return toCheck('questionnaire', 'info', 'Status unavailable')
}

export interface EventReadiness {
  /** The per-event checks, in dot order (all but the booking-wide Questionnaire). */
  checks: LogisticsCheck[]
  ready: number
  total: number
  /** The first counted step not done, or undefined when the event is all set. */
  next?: LogisticsCheck
}

/** A multi-event summary row's readiness over the per-event checks. */
export function logisticsEventReadiness(event: JobEventLite): EventReadiness {
  const checks = logisticsChecklist(event).filter((check) => check.key !== 'questionnaire')
  const { ready, total } = logisticsReadyCount(checks)
  return {
    checks,
    ready,
    total,
    next: checks.find((check) => isStatefulCheck(check) && check.mark !== 'done'),
  }
}
